import { UseFormReturn } from "react-hook-form";
import { DrawerClose, DrawerHeader } from "@/components/ui/drawer";
import { FormField } from "@/components/ui/form";
import { Step } from "./constants";
import { cn, formatDate, currencyFormatter } from "@/lib/utils";
import { CaretRight } from "@phosphor-icons/react";
import { Button, buttonVariants } from "@/components/ui/button";
import FilePreview from "@/components/custom/file-preview";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import ScrollableCalendar from "@/components/custom/scrollable-calendar";
import { Plus, X } from "lucide-react";
import { Vendor, Currency, Vessel, Account, Budget } from "@/lib/types";
import { ExpenseFormValues } from "./";
import {
  useGetBudgetCategories,
  useGetBudgetSubCategories,
} from "@/services/queries/budgets";

const InitialStep = ({
  form,
  setStep,
  currencies,
  vessels,
  accounts,
  budgets,
  vendors,
  title,
  selectedAccount,
  exchangeRate,
  isSubmitting,
}: {
  form: UseFormReturn<ExpenseFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  currencies: Currency[];
  vessels: Vessel[];
  accounts: Account[];
  budgets: Budget[];
  vendors: Vendor[];
  title?: string;
  selectedAccount?: Account;
  /** Current rate from useConversionRate — display value for the button. */
  exchangeRate: number | undefined;
  /** Disables Save while a submission is in flight to prevent duplicate expenses. */
  isSubmitting?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <DrawerHeader
        title={title ?? t("title.add_expense")}
        leftButton={
          <DrawerClose asChild>
            <button
              type="button"
              className="text-[15px] font-medium leading-5 text-[#45D3EC]"
            >
              {t("button.cancel")}
            </button>
          </DrawerClose>
        }
        rightButton={
          <button
            data-testid="expense-form-submit-button"
            type="submit"
            disabled={isSubmitting}
            className="text-[15px] font-medium leading-5 text-[#45D3EC] disabled:opacity-50"
          >
            {t("button.save")}
          </button>
        }
      />

      <div className="mt-[30px] w-full space-y-[15px]">
        {/* 1. Cost Center (Vessel) - sets context */}
        <CostCenterButton vessels={vessels} form={form} setStep={setStep} />

        {/* 2. Payment Method (Account with Balance + Currency) */}
        <PaymentMethodButton
          accounts={accounts}
          form={form}
          setStep={setStep}
          currencies={currencies}
        />

        {/* 3. Amount */}
        <AmountButton form={form} setStep={setStep} />

        {/* 4. Currency (transaction currency, can differ from account) */}
        <CurrencyButton currencies={currencies} form={form} setStep={setStep} />

        {/* 5. Exchange Rate (if currencies differ) */}
        {selectedAccount &&
          form.watch("currencyId") &&
          selectedAccount?.currencyId !== form.watch("currencyId") && (
            <ExchangeRateButton
              form={form}
              setStep={setStep}
              transactionCurrency={currencies.find(
                (c) => c.id === form.watch("currencyId")
              )}
              accountCurrency={currencies.find(
                (c) => c.id === selectedAccount?.currencyId
              )}
              exchangeRate={exchangeRate}
            />
          )}

        {/* 6. Budget */}
        <BudgetButton budgets={budgets} form={form} setStep={setStep} />

        {/* 7. Budget Category */}
        <BudgetCategoryButton form={form} setStep={setStep} />

        {/* 8. Vendor/Supplier */}
        <VendorButton form={form} setStep={setStep} vendors={vendors} />

        {/* 9. Date */}
        <DateButton form={form} />

        {/* 10. Files */}
        <UploadFilesButton form={form} />

        {/* 11. Notes */}
        <NoteButton form={form} setStep={setStep} />
      </div>
    </>
  );
};

export default InitialStep;

const CurrencyButton = ({
  currencies,
  form,
  setStep,
}: {
  currencies: Currency[];
  form: UseFormReturn<ExpenseFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslation();

  const currency = useMemo(
    () =>
      currencies.find((currency) => currency.id === form.watch("currencyId")),
    [currencies, form]
  );

  return (
    <>
      {form.getValues("currencyId") ? (
        <button
          data-testid="expense-form-currency-button"
          type="button"
          onClick={() => setStep(Step.CURRENCY)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.currency")}
          </span>
          <p className="flex items-center gap-1.5 text-start text-[15px] text-white">
            <span>{currency?.symbol}</span>
            <span>{t(`currency.${currency?.code.toLocaleLowerCase()}`)}</span>
          </p>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.currencyId}
          data-testid="expense-form-currency-button"
          onClick={() => setStep(Step.CURRENCY)}
          size="lg"
          variant="outline"
          className={cn("flex w-full items-center justify-between gap-2", {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.currencyId,
          })}
        >
          {t("label.currency")}
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};

const VendorButton = ({
  form,
  setStep,
  vendors,
}: {
  form: UseFormReturn<ExpenseFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  vendors: Vendor[];
}) => {
  const { t } = useTranslation();
  const [isEnteringVendor, setIsEnteringVendor] = useState(false);
  const vendor = form.watch("vendor");

  const isEnteringVendorExists = useMemo(() => {
    return (
      typeof vendor === "string" &&
      vendors.find((v) => v.name.toLowerCase() === vendor.toLowerCase())
    );
  }, [vendor, vendors]);

  useEffect(() => {
    if (typeof vendor === "string") {
      setIsEnteringVendor(true);
    }
  }, [vendor]);

  return (
    <div className="flex flex-row items-center justify-between gap-3">
      {!isEnteringVendor ? (
        (vendor as { id: string })?.id ? (
          <button
            data-testid="expense-form-vendor-button"
            type="button"
            onClick={() => setStep(Step.VENDOR)}
            className="flex w-full flex-row items-center justify-between rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
          >
            <div className="flex flex-col items-start justify-between">
              <span className="text-xs leading-4 text-white/50">
                {t("label.supplier")}
              </span>
              <p className="text-start text-[15px] text-white">
                {
                  vendors.find((v) => v.id === (vendor as { id: string }).id)
                    ?.name
                }
              </p>
            </div>

            <div
              data-testid="expense-form-clear-vendor-button"
              role="button"
              onClick={(e) => {
                e.stopPropagation();
                form.setValue("vendor", null);
              }}
            >
              <X size={16} color="#E3EDFC" />
            </div>
          </button>
        ) : (
          <Button
            data-invalid={!!form.formState.errors.vendor}
            data-testid="expense-form-vendor-button"
            onClick={() => setStep(Step.VENDOR)}
            size="lg"
            variant="outline"
            className={cn("flex w-full items-center justify-between gap-2", {
              "border-red-500 hover:border-red-500 active:border-red-500":
                form.formState.errors.vendor,
            })}
          >
            {t("label.supplier")}
            <CaretRight width={10} color="#E3EDFC" />
          </Button>
        )
      ) : (
        <input
          data-testid="expense-form-new-vendor-input"
          className={cn(
            "h-[43px] w-full rounded-lg border border-solid border-white/[.33] bg-transparent px-[15px] py-[10px] text-white outline-none",
            {
              "border-red-500 hover:border-red-500 active:border-red-500":
                form.formState.errors.vendor,
            }
          )}
          placeholder={t("label.enter_new_supplier_name")}
          value={form.watch("vendor")?.toString() ?? ""}
          onChange={(e) => {
            form.setValue("vendor", e.target.value);
            form.trigger("vendor");
          }}
        />
      )}

      {(typeof vendor === "string" || !vendor) && (
        <div className="flex flex-row items-center justify-between gap-2">
          {/* NOTE: Add this button when vendor payment methods are needed
          isEnteringVendor && (
            <Button
              disabled={
                !(
                  typeof form.watch("vendor") === "string" &&
                  (form.watch("vendor")?.toString().length ?? 0) > 0
                )
              }
              onClick={() => {
                if (isEnteringVendorExists) {
                  setIsEnteringVendor(false);
                  form.setValue("vendor", isEnteringVendorExists);
                } else {
                  setStep(Step.VENDOR_PAYMENT_DETAILS);
                }
              }}
              data-testid="expense-form-add-new-vendor-button"
              type="button"
              className="h-[43px] w-[43px] min-w-[43px] items-center"
            >
              <Plus size={16} color="#E3EDFC" />
            </Button>
          )
            */}
          <button
            data-testid={
              !isEnteringVendor
                ? "expense-form-add-new-vendor-button"
                : "expense-form-cancel-new-vendor-button"
            }
            type="button"
            onClick={() => {
              setIsEnteringVendor(!isEnteringVendor);
              form.setValue("vendor", null);

              // Clear the vendor payment methods when entering a new vendor input is disappeared
              if (isEnteringVendor) {
                form.setValue("vendorPaymentMethods", []);
              }
            }}
            className="flex h-[43px] w-[43px] min-w-[43px] items-center justify-center rounded-lg border border-solid border-white/[.33]"
          >
            {!isEnteringVendor ? (
              <Plus size={16} color="#E3EDFC" />
            ) : (
              <X size={16} color="#E3EDFC" />
            )}
          </button>
        </div>
      )}
    </div>
  );
};

const BudgetButton = ({
  form,
  setStep,
  budgets,
}: {
  form: UseFormReturn<ExpenseFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  budgets: Budget[];
}) => {
  const { t } = useTranslation();

  const budget = useMemo(
    () => budgets.find((budget) => budget.id === form.watch("budgetId")),
    [budgets, form]
  );

  return (
    <>
      {budget?.id ? (
        <button
          data-testid="expense-form-budget-button"
          type="button"
          onClick={() => setStep(Step.BUDGET)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.budget")}
          </span>
          <p className="text-start text-[15px] text-white">{budget?.name}</p>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.budgetId}
          data-testid="expense-form-budget-button"
          onClick={() => setStep(Step.BUDGET)}
          size="lg"
          variant="outline"
          className={cn("flex w-full items-center justify-between gap-2", {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.budgetId,
          })}
        >
          {t("label.budget")}
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};

const BudgetCategoryButton = ({
  form,
  setStep,
}: {
  form: UseFormReturn<ExpenseFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslation();

  const { data: categoriesData } = useGetBudgetCategories(
    form.watch("budgetId") || ""
  );
  const { data: subcategoriesData } = useGetBudgetSubCategories({
    id: form.watch("budgetCategoryId") || "",
  });

  const categories = useMemo(
    () => categoriesData?.value || [],
    [categoriesData]
  );
  const subcategories = useMemo(
    () => subcategoriesData?.value || [],
    [subcategoriesData]
  );

  const selectedCategory = useMemo(
    () => categories.find((cat) => cat.id === form.watch("budgetCategoryId")),
    [categories, form]
  );

  const selectedSubcategory = useMemo(
    () =>
      subcategories.find((sub) => sub.id === form.watch("budgetSubcategoryId")),
    [subcategories, form]
  );

  const displayText = selectedSubcategory
    ? `${selectedCategory?.name} / ${selectedSubcategory.name}`
    : selectedCategory?.name;

  if (displayText) {
    return (
      <button
        data-testid="expense-form-budget-category-button"
        type="button"
        onClick={() => setStep(Step.CATEGORIES)}
        className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
      >
        <span className="text-xs leading-4 text-white/50">
          {t("label.budget_category")}
        </span>
        <p className="text-start text-[15px] text-white">{displayText}</p>
      </button>
    );
  }

  return (
    <Button
      data-testid="expense-form-budget-category-button"
      onClick={() => setStep(Step.CATEGORIES)}
      size="lg"
      variant="outline"
      className="flex w-full items-center justify-between gap-2"
      disabled={!form.watch("budgetId")}
    >
      {t("label.budget_category")}
      <CaretRight width={10} color="#E3EDFC" />
    </Button>
  );
};

const PaymentMethodButton = ({
  form,
  setStep,
  accounts,
  currencies,
}: {
  form: UseFormReturn<ExpenseFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  accounts: Account[];
  currencies: Currency[];
}) => {
  const { t } = useTranslation();

  const paymentMethod = useMemo(
    () => accounts.find((account) => account.id === form.watch("accountId")),
    [accounts, form]
  );

  const accountCurrency = useMemo(
    () => currencies.find((c) => c.id === paymentMethod?.currencyId),
    [currencies, paymentMethod?.currencyId]
  );

  return (
    <>
      {paymentMethod?.accountNumber ? (
        <button
          data-testid="expense-form-payment-method-button"
          type="button"
          onClick={() => setStep(Step.PAYMENT_METHOD)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.payment_method")}
          </span>
          <p className="flex items-center gap-1 text-[15px] text-white">
            <span>{paymentMethod?.name}</span>
            <span className="text-white/50">
              ({paymentMethod?.accountNumber.replace(/^(.{15})/, "**** ")})
            </span>
          </p>
          {/* Balance and Currency display */}
          <div className="mt-1 flex items-center gap-2">
            <span className="text-sm font-semibold text-[#45D3EC]">
              {currencyFormatter(
                paymentMethod?.currentBalance,
                accountCurrency?.code ?? "USD"
              )}
            </span>
            <span className="text-xs text-white/50">
              {t("label.available")}
            </span>
          </div>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.accountId}
          data-testid="expense-form-payment-method-button"
          onClick={() => setStep(Step.PAYMENT_METHOD)}
          size="lg"
          className={cn(
            "flex w-full flex-row items-center justify-between gap-2",
            {
              "border-red-500 hover:border-red-500 active:border-red-500":
                form.formState.errors.accountId,
            }
          )}
          variant="outline"
        >
          {t("label.payment_method")}
          <CaretRight size={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};

const DateButton = ({ form }: { form: UseFormReturn<ExpenseFormValues> }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative w-full">
      <div
        data-invalid={!!form.formState.errors.transactionDate}
        data-testid="expense-form-date-button"
        role="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          form.getValues("transactionDate")
            ? "flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
            : `flex w-full flex-row items-center !justify-between gap-2 ${buttonVariants(
                {
                  variant: "outline",
                  size: "lg",
                }
              )}`,
          {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.transactionDate,
          }
        )}
      >
        {form.getValues("transactionDate") ? (
          <>
            <span className="text-xs leading-4 text-white/50">
              {t("label.date")}
            </span>
            <span className="text-start text-[15px] text-white">
              {formatDate(
                form.getValues("transactionDate")!,
                "dd MMMM yyyy - HH:mm"
              )}
            </span>
          </>
        ) : (
          <div className="flex w-full flex-row items-center justify-between gap-2">
            {t("label.date")}
            <CaretRight width={10} color="#E3EDFC" />
          </div>
        )}
      </div>

      {isOpen && (
        <ScrollableCalendar
          data-testid="expense-form-date-calendar"
          date={
            form.getValues("transactionDate")
              ? new Date(form.getValues("transactionDate")!)
              : new Date()
          }
          setDate={(date) => {
            form.setValue("transactionDate", date);
            form.trigger("transactionDate");
          }}
        />
      )}
    </div>
  );
};

const UploadFilesButton = ({
  form,
}: {
  form: UseFormReturn<ExpenseFormValues>;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <label
        data-invalid={!!form.formState.errors.files}
        data-testid="expense-form-files-label"
        htmlFor="expense-files"
        className={cn(
          form.getValues("files") && form.getValues("files").length > 0
            ? "flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
            : `flex w-full flex-row items-center justify-between gap-2 ${buttonVariants(
                {
                  variant: "outline",
                  size: "lg",
                }
              )}`,
          {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.files,
          }
        )}
      >
        {form.getValues("files").length > 0 ? (
          <>
            <span className="text-xs leading-4 text-white/50">
              {t("label.files")}
            </span>
            <FilePreview
              className="mt-1"
              files={
                "url" in form.watch("files")[0]
                  ? undefined
                  : (form.watch("files") as unknown as File[])
              }
              urls={
                "url" in form.watch("files")[0]
                  ? (form.watch("files") as { id: string; url: string }[]).map(
                      (file) => file.url
                    )
                  : undefined
              }
              onRemove={(file) => {
                if (typeof file === "string") {
                  form.setValue(
                    "files",
                    (
                      form.watch("files") as { id: string; url: string }[]
                    ).filter((f) => f.url !== file)
                  );
                } else {
                  form.setValue(
                    "files",
                    (form.watch("files") as unknown as File[]).filter(
                      (f) => f !== file
                    ) as unknown as { id: string; url: string }[]
                  );
                }
              }}
            />
          </>
        ) : (
          <div className="flex w-full flex-row items-center justify-between gap-2">
            {t("label.files")}
            <CaretRight width={10} color="#E3EDFC" />
          </div>
        )}
      </label>
      <FormField
        control={form.control}
        name="files"
        render={({ field: { value, onChange, ...rest } }) => (
          <input
            data-testid="expense-form-files-input"
            id="expense-files"
            type="file"
            multiple
            accept="image/*,application/pdf"
            hidden
            onChange={(e) => {
              form.watch("files");
              onChange(Array.from(e.target.files || []));
            }}
            {...rest}
          />
        )}
      />
    </>
  );
};

const NoteButton = ({
  form,
  setStep,
}: {
  form: UseFormReturn<ExpenseFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslation();
  return (
    <>
      {form.getValues("description") &&
      form.getValues("description").trim() !== "" ? (
        <button
          data-testid="expense-form-note-button"
          type="button"
          onClick={() => setStep(Step.NOTE)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.notes")}
          </span>
          <p className="text-start text-[15px] text-white">
            {form.getValues("description")}
          </p>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.description}
          data-testid="expense-form-note-button"
          onClick={() => setStep(Step.NOTE)}
          size="lg"
          variant="outline"
          className={cn("w-full", {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.description,
          })}
        >
          <div className="flex w-full flex-row items-center justify-start gap-2">
            {t("label.add_note")}
            <span className="text-base text-white/50">
              ({t("label.optional")})
            </span>
          </div>
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};

const CostCenterButton = ({
  form,
  setStep,
  vessels,
}: {
  form: UseFormReturn<ExpenseFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  vessels: Vessel[];
}) => {
  const { t } = useTranslation();
  const costCenter = useMemo(
    () => vessels.find((vessel) => vessel.id === form.watch("vesselId")),
    [vessels, form]
  );

  return (
    <>
      {costCenter?.id ? (
        <button
          data-testid="expense-form-cost-center-button"
          type="button"
          onClick={() => setStep(Step.COST_CENTER)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.cost_center")}
          </span>
          <p className="text-start text-[15px] text-white">
            {costCenter?.name}
          </p>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.vesselId}
          data-testid="expense-form-cost-center-button"
          onClick={() => setStep(Step.COST_CENTER)}
          size="lg"
          variant="outline"
          className={cn("flex w-full items-center justify-between gap-2", {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.vesselId,
          })}
        >
          {t("label.cost_center")}
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};

const AmountButton = ({
  form,
  setStep,
}: {
  form: UseFormReturn<ExpenseFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslation();
  return (
    <>
      {form.getValues("amount") ? (
        <button
          data-testid="expense-form-amount-button"
          type="button"
          onClick={() => setStep(Step.AMOUNT)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.amount")}
          </span>
          <p className="text-start text-[15px] text-white">
            {form.getValues("amount")}
          </p>
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.amount}
          data-testid="expense-form-amount-button"
          onClick={() => setStep(Step.AMOUNT)}
          size="lg"
          variant="outline"
          className={cn("w-full", {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.amount,
          })}
        >
          <div className="flex w-full flex-row items-center justify-start gap-2">
            {t("label.add_amount")}
          </div>
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};

const ExchangeRateButton = ({
  form,
  setStep,
  transactionCurrency,
  accountCurrency,
  exchangeRate,
}: {
  form: UseFormReturn<ExpenseFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  transactionCurrency?: Currency;
  accountCurrency?: Currency;
  /** Display value sourced from useConversionRate (not form state) so a context change updates the display in the same paint. */
  exchangeRate: number | undefined;
}) => {
  const { t } = useTranslation();
  const transactionCode = transactionCurrency?.code || "???";
  const accountCode = accountCurrency?.code || "???";

  // Format the rate display showing both directions
  const formatRateDisplay = () => {
    if (!exchangeRate || exchangeRate <= 0) return null;
    const inverseRate = 1 / exchangeRate;
    return (
      <div className="flex flex-col items-start gap-0.5">
        <span className="text-[15px] text-white">
          1 {accountCode} = {inverseRate.toFixed(6)} {transactionCode}
        </span>
        <span className="text-xs text-white/50">
          1 {transactionCode} = {exchangeRate.toFixed(6)} {accountCode}
        </span>
      </div>
    );
  };

  return (
    <>
      {exchangeRate ? (
        <button
          data-testid="expense-form-exchange-rate-button"
          type="button"
          onClick={() => setStep(Step.EXCHANGE_RATE)}
          className="flex w-full flex-col items-start justify-center rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg"
        >
          <span className="text-xs leading-4 text-white/50">
            {t("label.exchange_rate")}
          </span>
          {formatRateDisplay()}
        </button>
      ) : (
        <Button
          data-invalid={!!form.formState.errors.exchangeRate}
          data-testid="expense-form-exchange-rate-button"
          onClick={() => setStep(Step.EXCHANGE_RATE)}
          size="lg"
          variant="outline"
          className={cn("w-full", {
            "border-red-500 hover:border-red-500 active:border-red-500":
              form.formState.errors.exchangeRate,
          })}
        >
          <div className="flex w-full flex-row items-center justify-start gap-2">
            {t("label.add_exchange_rate")} ({transactionCode} → {accountCode})
          </div>
          <CaretRight width={10} color="#E3EDFC" />
        </Button>
      )}
    </>
  );
};
